import { multiplierFor } from '@/lib/money/index.js';

const GRID = 25n;
const TILES = 25;
const RTP_NUM = 945n; // 0.945 × 1000
const RTP_DEN = 1000n;

export const BP = 10000n;

/**
 * The max-win clamp is NOT mirrored here. svc-game applies it as
 * `scaleThbMinor(MINES_CONFIG.maxWinMinor, currency)`, so the number depends on
 * the agent's currency: a THB literal here promised a 0dp agent 100x the
 * ceiling the server enforces, and clipped a 3dp agent 10x below it. The
 * already-scaled value arrives on the mines public config as `maxWinMinor` and
 * is threaded in — a non-positive cap means the server did not state one, in
 * which case nothing is clamped rather than clamped to the wrong currency.
 */
function clamp(raw: bigint, maxWinMinor: bigint): bigint {
  if (maxWinMinor <= 0n) return raw;
  return raw < maxWinMinor ? raw : maxWinMinor;
}

/**
 * Mirror of svc-game's mines `multiplierBp` (apps/game/src/originals/mines/multiplier.ts):
 * floor( 0.945 × Π_{i=0..k-1} (25-i)/(25-mines-i) × 10000 ). Client-side preview only —
 * the authoritative value still comes from the server per reveal.
 */
export function multiplierBp(k: number, mines: number): bigint {
  if (k <= 0) return 0n;
  const M = BigInt(mines);
  let num = 1n;
  let den = 1n;
  for (let i = 0n; i < BigInt(k); i++) {
    num *= GRID - i;
    den *= GRID - M - i;
  }
  return (num * RTP_NUM * BP) / (den * RTP_DEN);
}

export function payoutMinor(bet: bigint, bp: bigint, maxWinMinor: bigint): bigint {
  return clamp((bet * bp) / BP, maxWinMinor);
}

/**
 * Multiplier actually payable on `bet` once the max-win clamp applies — every
 * displayed ×mult must come through here so the UI never advertises a multiplier
 * the server would clip at cashout.
 */
export function cappedMultiplierBp(bet: bigint, bp: bigint, maxWinMinor: bigint): bigint {
  if (bet <= 0n || maxWinMinor <= 0n) return bp;
  if ((bet * bp) / BP < maxWinMinor) return bp;
  return (maxWinMinor * BP) / bet;
}

/**
 * Chance (whole percent) that the NEXT tile revealed is safe, given the mines
 * count and how many safe tiles are already open. Drives the suspense meter.
 */
export function nextTileSurvivalPct(minesCount: number, revealedSafe: number): number {
  const remaining = TILES - revealedSafe;
  if (remaining <= 0) return 0;
  const safeRemaining = remaining - minesCount;
  if (safeRemaining <= 0) return 0;
  return Math.round((safeRemaining / remaining) * 100);
}

/**
 * The cap to assume when the mines config carries none.
 *
 * "No cap" is the right reading for wheel, keno and scratch — their server
 * configs really do set `maxWinMinor: 0n`. It is the WRONG reading for mines,
 * where `originals/routes.ts:127` publishes a positive, currency-scaled cap
 * unconditionally: an absent field there means the SPA is talking to a build of
 * svc-game from before P6, and the player SPA ships through Cloudflare Pages
 * while svc-game ships through ArgoCD, so that skew window is a real one. In it
 * the preview showed a ฿1.00 / 12-mine full clear as ฿4,914,283.50 while the
 * server pays ฿500,000.00 — a 9.83x over-promise in the ladder, the climb strip
 * and the live cashout button at once.
 *
 * This IS a scaled mirror of a server constant, which P6 removed on purpose.
 * Its objection was to a THB LITERAL — one that "promised a 0dp agent 100x the
 * ceiling" — and scaling by `multiplierFor` answers exactly that. What is left
 * is drift, and `max-win-fallback.test.ts` restates the server's formula and
 * its constant so drift fails in CI rather than in a player's ladder. A stale
 * cap during a skew window under-promises by a bounded amount; no cap at all
 * over-promises by an unbounded one.
 */
export function minesFallbackMaxWinMinor(currency: string | undefined | null): bigint {
  if (!currency) return MINES_MAX_WIN_THB_MINOR;
  return (MINES_MAX_WIN_THB_MINOR * multiplierFor(currency)) / 100n;
}

/** Mirrors `MINES_CONFIG.maxWinMinor` in `apps/game/src/originals/mines/config.ts`. */
const MINES_MAX_WIN_THB_MINOR = 50_000_000n;

/**
 * What the preview should clamp at, given what the config published.
 *
 * ABSENT is not zero, and that distinction is the whole of W11. A published
 * `'0'` is a real answer — wheel, keno and scratch carry `maxWinMinor: 0n`
 * server-side and genuinely do not clamp. An absent field on MINES, where
 * `originals/routes.ts` publishes a positive cap unconditionally, means the SPA
 * is ahead of svc-game, and reading that as "no cap" is what over-promised.
 */
export function resolveMinesMaxWinMinor(
  published: string | undefined | null,
  currency: string | undefined | null,
): bigint {
  if (published === undefined || published === null) return minesFallbackMaxWinMinor(currency);
  return BigInt(published);
}
